import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Loader2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import VolumeControl from './VolumeControl';
import VideoPlayPauseButton from './VideoPlayPauseButton';
import { getCloudflarePosterUrl } from '../utils/cloudflareStream';
import useScrollAutoplay from '../hooks/useScrollAutoplay';

const MediaUploadHero = ({ 
  title, 
  description, 
  ctaText = "Get started",
  onCtaClick,
  initialMedia = null,
  initialMediaType = null
}) => {
  const [uploadedMedia, setUploadedMedia] = useState(initialMedia);
  const [mediaType, setMediaType] = useState(initialMediaType);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Only play the video while it is scrolled into view — pauses it off-screen.
  // Re-attaches when the video mounts after an upload (initial state is the
  // empty-drop-zone placeholder, not a <video> element).
  useScrollAutoplay(videoRef, isPlaying, 0.25, uploadedMedia && mediaType === 'video');

  const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200MB
  const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB
  const SUPPORTED_FORMATS = {
    image: ['image/jpeg', 'image/png', 'image/webp'],
    video: ['video/mp4', 'video/quicktime'],
  };

  const validateFile = (file) => {
    const isImage = SUPPORTED_FORMATS.image.includes(file.type);
    const isVideo = SUPPORTED_FORMATS.video.includes(file.type);

    if (!isImage && !isVideo) {
      toast.error('Unsupported file format. Please upload JPG, PNG, WebP, MP4, or MOV files.');
      return false;
    }

    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      toast.error(`File too large. Maximum size is ${maxSizeMB}MB for ${isVideo ? 'videos' : 'images'}.`);
      return false;
    }

    return true;
  };

  const handleFile = useCallback(async (file) => {
    if (!validateFile(file)) return;

    setIsLoading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const url = URL.createObjectURL(file);
      const type = file.type.startsWith('video/') ? 'video' : 'image';

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500));

      setUploadProgress(100);
      setTimeout(() => {
        setUploadedMedia(url);
        setMediaType(type);
        setIsLoading(false);
        setUploadProgress(0);
        clearInterval(progressInterval);
        toast.success('Media uploaded successfully!');
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      setIsLoading(false);
      setUploadProgress(0);
      toast.error('Failed to upload media. Please try again.');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    if (uploadedMedia) {
      URL.revokeObjectURL(uploadedMedia);
    }
    setUploadedMedia(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Media removed');
  }, [uploadedMedia]);

  const handleClick = useCallback(() => {
    if (!isLoading && !uploadedMedia) {
      fileInputRef.current?.click();
    }
  }, [isLoading, uploadedMedia]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section 
      className="group/video relative min-h-[85vh] overflow-hidden"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Background Media or Upload Zone */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          {uploadedMedia && mediaType === 'video' ? (
            <motion.video
              key="video"
              ref={videoRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              src={uploadedMedia}
              loop
              muted={isMuted}
              playsInline
              preload="metadata"
              poster={getCloudflarePosterUrl(uploadedMedia)}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="media-upload-hero-video"
            />
          ) : uploadedMedia && mediaType === 'image' ? (
            <motion.img
              key="image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              src={uploadedMedia}
              alt="Uploaded background"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`absolute inset-0 bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7] flex items-center justify-center cursor-pointer transition-all duration-300 ${
                isDragging ? 'bg-[#E8A0B8]/10' : ''
              }`}
              onClick={handleClick}
            >
              <div 
                className={`max-w-2xl mx-auto text-center p-12 border-4 border-dashed rounded-2xl transition-all duration-300 ${
                  isDragging 
                    ? 'border-[#E8A0B8] bg-[#E8A0B8]/5 scale-105' 
                    : 'border-black/20 bg-white/30'
                }`}
              >
                <Upload 
                  className={`w-16 h-16 mx-auto mb-6 transition-colors duration-300 ${
                    isDragging ? 'text-[#E8A0B8]' : 'text-black/40'
                  }`} 
                />
                <h3 className="text-2xl font-medium text-black/90 mb-3">
                  Drop your image or video here
                </h3>
                <p className="text-black/60 mb-2">
                  or click to browse your files
                </p>
                <p className="text-sm text-black/50 mt-4">
                  Supports JPG, PNG, WebP (up to 20MB) | MP4, MOV (up to 200MB)
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dark Gradient Overlay */}
        {uploadedMedia && (
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 40%, transparent 70%)'
            }}
          />
        )}
      </div>

      {/* Loading State */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30"
          >
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4">
              <Loader2 className="w-12 h-12 text-[#E8A0B8] animate-spin mx-auto mb-4" />
              <p className="text-center text-black/90 font-medium mb-3">
                Uploading media...
              </p>
              <div className="w-full bg-black/10 rounded-full h-2 overflow-hidden">
                <motion.div
                  className="h-full bg-[#E8A0B8]"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-center text-sm text-black/60 mt-2">
                {uploadProgress}%
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Control Buttons - Centered play/pause on hover + bottom-right mute (only for video media) */}
      {uploadedMedia && mediaType === 'video' && !isLoading && (
        <>
          {/* Centered play/pause overlay (hover-only). */}
          <VideoPlayPauseButton
            isPlaying={isPlaying}
            onClick={togglePlayPause}
            testId="media-upload-play-button"
          />

          <div className="absolute bottom-6 right-6 z-20 flex items-center gap-3">
            {/* Mute/Unmute Toggle Button with Hover Volume Slider */}
            <VolumeControl
              videoRef={videoRef}
              isMuted={isMuted}
              onToggleMute={toggleMute}
              testId="product-b-volume-button"
            />
          </div>
        </>
      )}

      {/* Content Overlay */}
      <div className="relative z-10 flex items-center min-h-[85vh]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium mb-4 sm:mb-6 leading-tight"
              style={{
                color: uploadedMedia ? 'white' : 'rgba(0, 0, 0, 0.95)',
                textShadow: uploadedMedia ? '0 2px 20px rgba(0, 0, 0, 0.5)' : 'none'
              }}
            >
              {title}
            </h1>
            <p 
              className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-10 max-w-3xl leading-relaxed"
              style={{
                color: uploadedMedia ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.70)',
                textShadow: uploadedMedia ? '0 2px 15px rgba(0, 0, 0, 0.5)' : 'none'
              }}
            >
              {description}
            </p>
            <Button
              size="lg"
              onClick={onCtaClick}
              className={`text-base px-8 py-6 ${
                uploadedMedia 
                  ? 'bg-white text-black hover:bg-white/90' 
                  : 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105'
              } transition-all duration-300 shadow-xl`}
            >
              {ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.mp4,.mov"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Upload media file"
      />
    </section>
  );
};

export default MediaUploadHero;
